/**
 * Sample data shapes for embedding real app components inside help
 * articles. Whenever an article wants to render a real component
 * (PrizeTrackerCard, MilestonesCard, dashboard hero, etc.) it pulls
 * realistic-looking props from this file rather than hand-rolling
 * inline mocks.
 *
 * Keep these consistent: Sarah Thompson is in Room 12 at Lincoln
 * Elementary in the Spring 2026 Read-a-thon. The classrooms, kids,
 * codes, donations, and totals all reference the same world so
 * articles linked to each other tell one story.
 */

// ── Anchors ──────────────────────────────────────────────────
export const SCHOOL = {
  id: 1,
  name: "Lincoln Elementary",
  slug: "lincoln-elementary",
  city: "Toronto",
  province: "ON",
};

export const MEMBERSHIP_ADMIN = { role: "school_admin", school_admin: true, teacher: false };
export const MEMBERSHIP_TEACHER = { role: "teacher", school_admin: false, teacher: true };

export const CAMPAIGN_ACTIVE = {
  id: 11,
  slug: "spring-2026",
  name: "Spring Read-a-thon 2026",
  state: "active",
  start_at: "2026-04-13T00:00:00Z",
  end_at: "2026-04-24T00:00:00Z",
  donations_open_at: "2026-03-30T00:00:00Z",
  grace_ends_at: "2026-05-01T00:00:00Z",
  raised_cents: 321000,
  minutes_logged: 22150,
  money_goal_cents: 500000,
  minute_goal: 50000,
  days_total: 12,
  days_elapsed: 7,
  days_remaining: 5,
  accepting_minutes: true,
  accepting_donations: true,
  path: "/schools/lincoln-elementary/dashboard?campaign=spring-2026",
};

export const CAMPAIGN_GRACE = {
  ...CAMPAIGN_ACTIVE,
  state: "grace",
  raised_cents: 521000,
  minutes_logged: 48400,
  days_elapsed: 12,
  days_remaining: 0,
  accepting_minutes: false,
  accepting_donations: true,
};

export const CAMPAIGN_LOCKED = {
  ...CAMPAIGN_GRACE,
  state: "locked",
  accepting_donations: false,
};

// ── School-wide milestone rewards ────────────────────────────
export const SCHOOL_MILESTONES = [
  {
    id: 101,
    kind: "money",
    threshold_amount: 100000, // $1,000
    prize: "Pizza party for everyone",
    unlocked: true,
    current_amount: 321000,
    progress_pct: 100,
  },
  {
    id: 102,
    kind: "money",
    threshold_amount: 250000, // $2,500
    prize: "Pajama day",
    unlocked: true,
    current_amount: 321000,
    progress_pct: 100,
  },
  {
    id: 103,
    kind: "money",
    threshold_amount: 500000, // $5,000
    prize: "Principal gets pied",
    unlocked: false,
    current_amount: 321000,
    progress_pct: 64,
  },
  {
    id: 104,
    kind: "minutes",
    threshold_amount: 50000,
    prize: "Whole-school silent reading hour",
    unlocked: false,
    current_amount: 22150,
    progress_pct: 44,
  },
];

// ── Classroom-wide milestone rewards ─────────────────────────
export const CLASSROOM_MILESTONES = [
  {
    id: 201,
    kind: "money",
    threshold_amount: 30000, // $300
    prize: "Pajama day for Room 12",
    unlocked: true,
    current_amount: 31000,
    progress_pct: 100,
  },
  {
    id: 202,
    kind: "money",
    threshold_amount: 75000, // $750
    prize: "Movie afternoon",
    unlocked: false,
    current_amount: 31000,
    progress_pct: 41,
  },
  {
    id: 203,
    kind: "minutes",
    threshold_amount: 2000,
    prize: "Read-outside Friday",
    unlocked: false,
    current_amount: 1830,
    progress_pct: 91,
  },
];

// ── Per-student prize templates (admin-managed) ──────────────
export const STUDENT_PRIZE_TEMPLATES = [
  {
    id: 301,
    kind: "minutes",
    threshold_amount: 500,
    prize: "Fidget spinner",
    achieved_count: 42,
    student_count: 184,
  },
  {
    id: 302,
    kind: "minutes",
    threshold_amount: 1500,
    prize: "Bookstore gift card",
    achieved_count: 3,
    student_count: 184,
  },
];

// ── Per-student prize achievements (teacher's tracker) ───────
// Grouped by prize template, mirroring the school-wide prize report.
export const PRIZES_FOR_TRACKER = [
  {
    id: 301,
    prize: "Fidget spinner",
    kind: "minutes",
    threshold_amount: 500,
    award_style: "unlock",
    achieved_count: 3,
    disbursed_count: 1,
    total_entries: 0,
    entrants_count: 0,
    achievements: [
      {
        id: 401,
        student_id: 11,
        student_name: "Sarah T.",
        achieved_at: "2026-04-18T15:14:00Z",
        disbursed: false,
        disbursed_at: null,
        disbursed_by: null,
        entry_count: 0,
        won: false,
        disburse_path: "#",
        undisburse_path: "#",
      },
      {
        id: 402,
        student_id: 12,
        student_name: "Marcus L.",
        achieved_at: "2026-04-20T11:02:00Z",
        disbursed: false,
        disbursed_at: null,
        disbursed_by: null,
        entry_count: 0,
        won: false,
        disburse_path: "#",
        undisburse_path: "#",
      },
      {
        id: 403,
        student_id: 13,
        student_name: "Priya K.",
        achieved_at: "2026-04-15T13:00:00Z",
        disbursed: true,
        disbursed_at: "2026-04-16T15:30:00Z",
        disbursed_by: "Ms. Lee",
        entry_count: 0,
        won: false,
        disburse_path: "#",
        undisburse_path: "#",
      },
    ],
  },
];

// ── Roster ───────────────────────────────────────────────────
export const CLASSROOM = {
  id: 31,
  name: "Room 12",
  grade: "Grade 5",
  student_count: 23,
  total_raised_cents: 31000,
  total_minutes_logged: 1830,
  money_goal_cents: 50000,
  minute_goal: 5000,
  teachers: ["Ms. Lee"],
};

export const STUDENTS_FOR_LOG = [
  { id: 11, full_name: "Sarah T.", checked: true },
  { id: 12, full_name: "Marcus L.", checked: true },
  { id: 13, full_name: "Priya K.", checked: false },
  { id: 14, full_name: "Ayaan P.", checked: true },
  { id: 15, full_name: "Lin T.", checked: true },
];

export const STUDENTS_FOR_ROSTER = [
  { id: 11, first_name: "Sarah", last_name: "T.", claim_code: "BEAR-MOON-4217", claimed: true,  raised_cents: 7500, minutes_logged: 510 },
  { id: 12, first_name: "Marcus", last_name: "L.",     claim_code: "EAGLE-RIVER-1936", claimed: true, raised_cents: 5000, minutes_logged: 502 },
  { id: 13, first_name: "Priya",  last_name: "K.",  claim_code: "FOX-CLOUD-0752",   claimed: false, raised_cents: 0,    minutes_logged: 480 },
  { id: 14, first_name: "Ayaan",  last_name: "P.",   claim_code: "WHALE-HILL-8841",  claimed: true,  raised_cents: 12500, minutes_logged: 290 },
];

// ── Student detail (parent view) ─────────────────────────────
export const STUDENT_DETAIL = {
  id: 11,
  full_name: "Sarah T.",
  classroom_name: "Room 12",
  grade: "Grade 5",
  school_name: "Lincoln Elementary",
  raised_cents: 7500,
  minutes_logged: 510,
  campaign_state: "active",
  campaign_name: "Spring Read-a-thon 2026",
  accepting_minutes: true,
  personal_minute_goal: 600,
  personal_money_goal_cents: 10000,
  public_page_path: "/p/dyq8s4-bear-moon-42",
  public_page_url: "https://pagepledge.com/p/dyq8s4-bear-moon-42",
  log_minutes_path: "#",
};

// ── Activity / batches / donations ──────────────────────────
export const ACTIVITY = [
  {
    id: 1,
    type: "minute_batch",
    summary: "Logged 20 min for 23 students",
    detail: "Charlotte's Web · ch 3",
    when: "2026-04-19T18:14:00Z",
    by: "Ms. Lee",
    can_undo: true,
    undo_path: "#",
  },
  {
    id: 2,
    type: "donation",
    summary: "Karen H. donated $50 to Sarah Thompson",
    detail: "Cover the fee · message: Go Sarah!",
    when: "2026-04-19T15:42:00Z",
    by: null,
    can_undo: false,
  },
  {
    id: 3,
    type: "minute_solo",
    summary: "Logged 15 min for Sarah Thompson",
    detail: "DEAR time, after lunch",
    when: "2026-04-19T13:02:00Z",
    by: "Ms. Lee",
    can_undo: true,
    undo_path: "#",
  },
];

export const TRANSACTIONS = [
  {
    id: 91,
    donor_name: "Karen H.",
    donor_email: "karen@example.com",
    student_name: "Sarah Thompson",
    amount_cents: 5000,
    fee_cents: 143,
    tip_cents: 0,
    total_charged_cents: 5143,
    status: "paid",
    paid_at: "2026-04-19T15:42:00Z",
  },
  {
    id: 92,
    donor_name: "Rosa Martinez",
    donor_email: "rosa@example.com",
    anonymous: true,
    student_name: "Marcus Lee",
    amount_cents: 2500,
    fee_cents: 0,
    tip_cents: 0,
    total_charged_cents: 2500,
    status: "paid",
    paid_at: "2026-04-18T20:14:00Z",
  },
  {
    id: 93,
    donor_name: "James K.",
    donor_email: "james@example.com",
    student_name: "Ayaan Patel",
    amount_cents: 1000,
    fee_cents: 37,
    tip_cents: 0,
    total_charged_cents: 1037,
    status: "refunded",
    paid_at: "2026-04-15T19:00:00Z",
  },
];

// ── Members ──────────────────────────────────────────────────
export const MEMBERS = [
  { id: 1, name: "Principal Adams", email: "principal@lincoln.ca", role: "school_admin", is_self: true,  signed_in: true,  update_path: "#", delete_path: "#" },
  { id: 2, name: "Ms. Lee",         email: "ms.lee@lincoln.ca",    role: "teacher",      is_self: false, signed_in: true,  update_path: "#", delete_path: "#" },
  { id: 3, name: "Mr. Patel",       email: "mr.patel@lincoln.ca",  role: "teacher",      is_self: false, signed_in: true,  update_path: "#", delete_path: "#" },
  { id: 4, name: "Mrs. Iqbal",      email: "mrs.iqbal@lincoln.ca", role: "teacher",      is_self: false, signed_in: false, update_path: "#", delete_path: "#" },
];

export const MEMBER_ROLES = [
  { value: "school_admin", label: "School admin" },
  { value: "teacher",      label: "Teacher" },
];

// ── Square connection ────────────────────────────────────────
// ── Setup guide (dashboard setup card) ───────────────────────
// Mirrors the props from SetupGuideProps#setup_summary_props. Tiers,
// phases and order match Onboarding::SetupGuide::STEPS.
const SETUP_STEP_SHAPES = [
  ["school_details", "foundations", "recommended"],
  ["campaign", "foundations", "required"],
  ["payment", "foundations", "required"],
  ["square", "foundations", "required"],
  ["roster", "people", "required"],
  ["teachers", "people", "recommended"],
  ["team", "people", "optional"],
  ["school_rewards", "rewards", "recommended"],
  ["student_prizes", "rewards", "optional"],
  ["teacher_info", "spread_the_word", "recommended"],
  ["family_info", "spread_the_word", "recommended"],
  ["take_home_sheets", "spread_the_word", "recommended"],
  ["notifications", "spread_the_word", "optional"],
  ["launch", "launch", "required"],
];

// locked: steps waiting on an unpaid campaign fee (shown with a padlock).
function setupSteps(statuses, dueOn = {}, locked = []) {
  return SETUP_STEP_SHAPES.map(([key, phase, tier]) => ({
    key,
    phase,
    tier,
    status: statuses[key] || "todo",
    locked: locked.includes(key),
    blocked_by: locked.includes(key) ? ["payment"] : [],
    waiting_on: [],
    due_on: dueOn[key] || null,
    overdue: false,
    skippable: !["school_details", "campaign", "payment", "square", "roster", "launch"].includes(key),
  }));
}

// A school that's just signed up (free trial): its first campaign is a
// draft whose dates aren't set yet, and the fee can be paid straight away.
export const SETUP_FRESH = {
  campaign: { id: 10, name: "Read-a-thon 2026", slug: "read-a-thon-2026", dates_confirmed: false },
  progress: { complete: 0, total: 13, required_remaining: 4 },
  next_step: "school_details",
  ready_to_launch: false,
  key_dates: { today: "2026-03-02" },
  setup_path: "#",
  trial: { plan: { tier: "small", name: "Small", student_limit: 300, price_cents: 50000 }, payment_path: "#", school_paid: false },
  steps: setupSteps(
    { square: "blocked", teachers: "blocked", teacher_info: "blocked", family_info: "blocked", take_home_sheets: "blocked", launch: "blocked" },
    {},
    ["square", "teachers", "launch"]
  ),
};

// Two weeks before donations open: foundations done (fee paid), students next.
export const SETUP_IN_PROGRESS = {
  campaign: { id: 11, name: "Spring Read-a-thon 2026", slug: "spring-2026", dates_confirmed: true },
  progress: { complete: 6, total: 13, required_remaining: 1 },
  next_step: "roster",
  ready_to_launch: false,
  key_dates: {
    today: "2026-03-16",
    donations_open_on: "2026-03-30",
    start_on: "2026-04-13",
    end_on: "2026-04-24",
    minutes_close_at: "Sat Apr 25, 9:00 AM",
    donations_close_on: "2026-05-01",
  },
  setup_path: "#",
  steps: setupSteps(
    { school_details: "done", campaign: "done", payment: "done", square: "done", team: "skipped", school_rewards: "done", teachers: "blocked", take_home_sheets: "blocked", launch: "blocked" },
    { roster: "2026-03-23", student_prizes: "2026-03-23", teacher_info: "2026-03-25", family_info: "2026-03-27", launch: "2026-04-13" }
  ),
};

